export interface ScrollColors {
  bg: string;
  bgSecondary: string;
  bgTertiary: string;
  bgElevated: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  border: string;
  accent: string;
  accentFg: string;
  switchTrack: string;
  error: string;
}

export const ACCENT_PRESETS = [
  '#1D1916', '#3D2D5C', '#D97706', '#EF4444',
  '#059669', '#4D7C0F', '#2563EB', '#7C3AED',
  '#EC4899', '#0891B2',
];

const DARK_MODE_MAP: Record<string, string> = {
  "#D97706": "#FBBF24",
  "#EF4444": "#F87171",
  "#DC2626": "#F87171",
  "#059669": "#34D399",
  "#4D7C0F": "#A3E635",
  "#2563EB": "#60A5FA",
  "#7C3AED": "#A78BFA",
  "#EC4899": "#F472B6",
  "#0891B2": "#22D3EE",
  "#3D2D5C": "#A78BFA",
};

/** Resolve a stored accent for the active theme. `#1D1916`/empty => fallback. */
export function resolveAccent(color: string | null | undefined, isDark: boolean, fallback: string): string {
  if (!color || color === "#1D1916") return fallback;
  if (isDark) return DARK_MODE_MAP[color] || color;
  return color;
}

/** Like resolveAccent but returns null for the neutral case (badge uses monochrome fallback then). */
export function resolveBadgeAccent(color: string | null | undefined, isDark: boolean): string | null {
  if (!color || color === "#1D1916") return null;
  return resolveAccent(color, isDark, color);
}

/** WCAG relative luminance of a #RRGGBB hex, 0..1. Non-hex => 0. */
export function relativeLuminance(hex: string): number {
  const h = hex.replace("#", "");
  if (h.length !== 6) return 0;
  const toLin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const r = toLin(parseInt(h.slice(0, 2), 16));
  const g = toLin(parseInt(h.slice(2, 4), 16));
  const b = toLin(parseInt(h.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Auto-contrast ink for a solid fill. Threshold 0.5 is tunable. */
export function pickInk(fillHex: string): "#FFFFFF" | "#1D1916" {
  return relativeLuminance(fillHex) > 0.5 ? "#1D1916" : "#FFFFFF";
}

export const lightColors: ScrollColors = {
  bg: "#FDFCFA",
  bgSecondary: "#F8F6F2",
  bgTertiary: "#F2EFEA",
  bgElevated: "#FFFFFF",
  text: "#1D1916",
  textSecondary: "rgba(29, 25, 22, 0.65)",
  textTertiary: "rgba(29, 25, 22, 0.45)",
  border: "rgba(29, 25, 22, 0.08)",
  accent: "#1D1916",
  accentFg: "#FDFCFA",
  switchTrack: "#1D1916",
  error: "#DC2626",
};

export const darkColors: ScrollColors = {
  bg: "#1D1916",
  bgSecondary: "#28231E",
  bgTertiary: "#332D27",
  bgElevated: "#332D27",
  text: "#FFF8EB",
  textSecondary: "rgba(255, 248, 235, 0.7)",
  textTertiary: "rgba(255, 248, 235, 0.45)",
  border: "rgba(255, 248, 235, 0.1)",
  accent: "#FFF8EB",
  accentFg: "#1D1916",
  switchTrack: "#8B7E6F",
  error: "#EF4444",
};
